import {
  Box,
  ListItemIcon,
  ListItemText,
  Tooltip as MUITooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MUIMenuItem from "@mui/material/MenuItem";
import { FC, type JSX, PropsWithChildren } from "react";
import { Link } from "react-router-dom";

import { MenuEntryInterface } from "../../types/menu-type";

// depending on whether or not a link is set, we need to either use a `Link` root element for the menu item or no root element at all.
// this FC takes care of that.
const RootElement: FC<
  PropsWithChildren<{
    link?: string;
  }>
> = ({ children, link }) =>
  link ? (
    <Link to={link} style={{ textDecoration: "none" }}>
      {children}
    </Link>
  ) : (
    children
  );

const Tooltip: FC<PropsWithChildren<{ title: string }>> = ({
  children,
  title,
}) => {
  return (
    <MUITooltip
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: "#fff",
            color: "#414141",
            border: "1px solid",
            borderColor: "background.paper",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            fontSize: "14px",
            fontWeight: 400,
            padding: "12px 16px",
            borderRadius: "8px",
          },
        },
      }}
      disableInteractive
      placement="right-end"
      title={title}
    >
      {children as JSX.Element}
    </MUITooltip>
  );
};

export const MenuItem: FC<MenuEntryInterface> = ({
  title,
  active,
  link,
  icon: ItemIcon,
  level,
}) => {
  const theme = useTheme();

  return (
    <RootElement link={link}>
      <Tooltip title={title}>
        <MUIMenuItem
          selected={active}
          data-testid="MenuItem"
          sx={[
            {
              color: active
                ? theme.palette.primary.main
                : theme.palette.grey["800"],
              marginLeft: level > 0 ? "26px" : "0px",
              padding: level > 0 ? "6px 16px 6px 10px" : "6px 16px",
              "& .MuiListItem-root": {
                display: "list-item",
              },
              "&.Mui-selected": {
                background: "none",
              },
              "&.Mui-selected:hover": {
                backgroundColor:
                  level === 0 ? theme.palette.secondary.main : "transparent",
              },
              ":hover": {
                backgroundColor:
                  level === 0 ? theme.palette.secondary.main : "transparent",
                fontWeight: 500,
                color: theme.palette.primary.main,
              },
            },
          ]}
        >
          {ItemIcon &&
            (typeof ItemIcon === "string" ? (
              <Box
                component="img"
                src={ItemIcon}
                sx={{
                  minWidth: "20px!important",
                  mr: (level || 0) > 0 ? 0 : "10px",
                }}
              />
            ) : (
              <ListItemIcon
                sx={{
                  minWidth: "20px!important",
                  mr: (level || 0) > 0 ? 0 : "10px",
                }}
              >
                <ItemIcon
                  sx={{
                    fontSize: (level || 0) > 0 ? ".3rem" : "1.5rem",
                    color: active
                      ? theme.palette.primary.main
                      : theme.palette.grey["500"],
                  }}
                />
              </ListItemIcon>
            ))}

          <ListItemText
            sx={{
              display: (level || 0) > 0 ? "list-item" : "",
              ml: (level || 0) > 0 ? "15px" : 0,
              ":hover": {
                fontWeight: 500,
                color: theme.palette.primary.main,
              },
            }}
          >
            <Typography
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                fontWeight: active ? 500 : undefined,
                ":hover": {
                  fontWeight: "inherit",
                  color: "inherit",
                },
              }}
            >
              {title}
            </Typography>
          </ListItemText>
        </MUIMenuItem>
      </Tooltip>
    </RootElement>
  );
};
